import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Check, Upload, Link, Music, Loader2, Play, Pause,
  Volume2, RotateCcw, Repeat, Clock, AlertCircle, HardDrive
} from 'lucide-react';
import uploadService from '../../../services/uploadService';
import { AssetLibraryModal } from '../assets';

const ACCEPTED_FORMATS = '.mp3,.wav,.aac,.ogg,.m4a';
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const AUDIO_CATEGORIES = [
  { id: 'background_music', name: 'Background Music', defaultDucking: true },
  { id: 'sound_effect', name: 'Sound Effect', defaultDucking: false },
  { id: 'intro_outro', name: 'Intro/Outro', defaultDucking: true },
  { id: 'jingle', name: 'Jingle', defaultDucking: true },
  { id: 'voice_over', name: 'Voice Over', defaultDucking: true },
  { id: 'other', name: 'Other', defaultDucking: false },
];

// Custom Select component - uses portal to avoid overflow clipping
const Select = ({ value, onValueChange, children, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current && !selectRef.current.contains(event.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !selectRef.current) return;
    const updatePosition = () => {
      const rect = selectRef.current.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;
      setDropdownStyle({
        position: 'fixed', left: rect.left, width: rect.width, zIndex: 99999,
        ...(openUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => { window.removeEventListener('scroll', updatePosition, true); window.removeEventListener('resize', updatePosition); };
  }, [isOpen]);

  const options = React.Children.toArray(children);
  const selectedOption = options.find(opt => opt.props.value === value);

  return (
    <div className="relative" ref={selectRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:border-white/20 transition-colors"
      >
        <span className={selectedOption ? '' : 'text-gray-500'}>
          {selectedOption ? selectedOption.props.children : placeholder}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && ReactDOM.createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.props.value}
              type="button"
              onClick={() => { onValueChange(option.props.value); setIsOpen(false); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                option.props.value === value ? 'text-green-400' : 'text-gray-300'
              }`}
            >
              {option.props.children}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

const SelectItem = ({ value, children }) => null;

// Custom Switch component
const Switch = ({ checked, onCheckedChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    onClick={() => onCheckedChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-[#6a1fbf]' : 'bg-white/20'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

// Custom Slider component
const Slider = ({ value, onValueChange, min, max, step }) => {
  const percentage = ((value[0] - min) / (max - min)) * 100;
  return (
    <div className="relative h-6 flex items-center">
      <div className="w-full h-2 bg-white/20 rounded-full">
        <div className="h-full bg-green-500 rounded-full" style={{ width: `${percentage}%` }} />
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value[0]}
        onChange={(e) => onValueChange([parseInt(e.target.value)])}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
      <div
        className="absolute w-4 h-4 bg-white rounded-full shadow-md pointer-events-none"
        style={{ left: `calc(${percentage}% - 8px)` }}
      />
    </div>
  );
};

// Tabs components
const Tabs = ({ value, onValueChange, children, className }) => {
  return (
    <div className={className}>
      {React.Children.map(children, child => {
        if (child.type === TabsList) {
          return React.cloneElement(child, { activeTab: value, setActiveTab: onValueChange });
        }
        if (child.type === TabsContent) {
          return child.props.value === value ? child : null;
        }
        return child;
      })}
    </div>
  );
};

const TabsList = ({ children, className, activeTab, setActiveTab }) => (
  <div className={`flex ${className}`}>
    {React.Children.map(children, child =>
      React.cloneElement(child, { activeTab, setActiveTab })
    )}
  </div>
);

const TabsTrigger = ({ value, children, className, activeTab, setActiveTab }) => (
  <button
    type="button"
    onClick={() => setActiveTab(value)}
    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors rounded-lg flex items-center justify-center gap-2 ${
      activeTab === value
        ? 'bg-white/10 text-white'
        : 'text-gray-400 hover:text-white'
    } ${className || ''}`}
  >
    {children}
  </button>
);

const TabsContent = ({ children, className }) => (
  <div className={className}>{children}</div>
);

function AudioPreview({ src, duration, fileSize, onRemove, onReplace }) {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => setPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="bg-white/5 rounded-xl overflow-hidden">
      <div className="p-4">
        <audio ref={audioRef} src={src} preload="metadata" />

        <div className="flex items-center gap-4">
          <button
            onClick={togglePlay}
            className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center hover:bg-green-500/30 transition-colors"
          >
            {playing ? (
              <Pause className="w-5 h-5 text-green-400" />
            ) : (
              <Play className="w-5 h-5 text-green-400 ml-0.5" />
            )}
          </button>

          <div className="flex-1">
            {/* Waveform visualization */}
            <div className="h-10 bg-white/5 rounded-lg flex items-center px-2 gap-0.5">
              {Array.from({ length: 40 }).map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-full ${i < (progress / 100) * 40 ? 'bg-green-500' : 'bg-white/20'}`}
                  style={{ height: `${20 + Math.sin(i * 0.5) * 15 + Math.random() * 10}%` }}
                />
              ))}
            </div>

            <div className="flex justify-between mt-1 text-xs text-gray-400">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 flex items-center justify-between">
        <div className="text-sm text-gray-400">
          {fileSize && <span>{fileSize}</span>}
        </div>
        <div className="flex gap-2">
          {onReplace && (
            <button onClick={onReplace} className="flex items-center gap-1 px-3 py-1.5 border border-white/20 rounded-lg text-white text-xs hover:bg-white/10 transition-colors">
              <RotateCcw className="w-3 h-3" />
              Replace
            </button>
          )}
          {onRemove && (
            <button onClick={onRemove} className="px-3 py-1.5 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-xs hover:bg-red-500/30 transition-colors">
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AudioElementModal({
  isOpen,
  onClose,
  onSave,
  audio,
  folders,
  currentFolder,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements
}) {
  const [sourceTab, setSourceTab] = useState('upload');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    src_type: 'upload',
    file_url: '',
    duration: 0,
    file_size: '',
    category: 'background_music',
    folder_id: currentFolder?.id || '',
    playback: {
      autoplay: false,
      loop: false,
      start_at: 0,
      end_at: null,
      volume: 80,
      ducking: true,
      fade_in: 0,
      fade_out: 0,
    }
  });

  const [isUploading, setIsUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlValidating, setUrlValidating] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [error, setError] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);

  const fileInputRef = useRef(null);

  // Initialize form data only when modal opens, not on every prop change
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (audio) {
        setFormData({
          ...formData,
          ...audio,
          playback: { ...formData.playback, ...audio.playback }
        });
        setSourceTab(audio.src_type || 'upload');
        if (audio.src_type === 'url') {
          setUrlInput(audio.file_url);
        }
      } else {
        setFormData(prev => ({
          ...prev,
          folder_id: currentFolder?.id || '',
          name: '',
          file_url: '',
          duration: 0
        }));
      }
      setError('');
      setHasInitialized(true);
    }
  }, [isOpen, audio, currentFolder, hasInitialized]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  // Update ducking default when category changes
  useEffect(() => {
    const category = AUDIO_CATEGORIES.find(c => c.id === formData.category);
    if (category && !audio) {
      updateField('playback.ducking', category.defaultDucking);
    }
  }, [formData.category]);

  const updateField = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setError('');

    const validTypes = ['audio/mpeg', 'audio/wav', 'audio/aac', 'audio/ogg', 'audio/mp4', 'audio/x-m4a'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|aac|ogg|m4a)$/i)) {
      setError('Unsupported format. Please use MP3, WAV, AAC, OGG, or M4A.');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadService.uploadFile(file);

      if (!result || !result.url) {
        throw new Error('No file URL returned from server');
      }

      updateField('file_url', result.url);
      updateField('src_type', 'upload');
      updateField('file_size', formatFileSize(file.size));

      if (!formData.name) {
        updateField('name', file.name.replace(/\.[^/.]+$/, ''));
      }

      // Get audio metadata
      const audioEl = document.createElement('audio');
      audioEl.preload = 'metadata';
      audioEl.onloadedmetadata = () => {
        updateField('duration', Math.round(audioEl.duration));
        updateField('playback.end_at', Math.round(audioEl.duration));
      };
      audioEl.src = result.url;

    } catch (err) {
      console.error('Upload error:', err);
      setError(`Upload failed: ${err.message || 'Unknown error'}. Please try again.`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUrlValidate = async () => {
    if (!urlInput.trim()) return;

    setUrlValidating(true);
    setUrlError('');

    try {
      const url = new URL(urlInput);
      if (!['http:', 'https:'].includes(url.protocol)) {
        setUrlError('Please enter an HTTP or HTTPS URL.');
        setUrlValidating(false);
        return;
      }
    } catch {
      setUrlError('Please enter a valid URL.');
      setUrlValidating(false);
      return;
    }

    const audioEl = document.createElement('audio');
    audioEl.src = urlInput;

    const timeout = setTimeout(() => {
      setUrlError('URL could not be reached. Please check the link.');
      setUrlValidating(false);
    }, 10000);

    audioEl.onloadedmetadata = () => {
      clearTimeout(timeout);
      updateField('file_url', urlInput);
      updateField('src_type', 'url');
      updateField('duration', Math.round(audioEl.duration));
      updateField('playback.end_at', Math.round(audioEl.duration));
      setUrlValidating(false);
    };

    audioEl.onerror = () => {
      clearTimeout(timeout);
      setUrlError('Could not load audio. Check if URL is accessible.');
      setUrlValidating(false);
    };
  };

  const handleAssetSelect = (asset) => {
    updateField('file_url', asset.file_url);
    updateField('src_type', 'upload');
    updateField('file_size', formatFileSize(asset.file_size || 0));
    if (!formData.name) {
      updateField('name', asset.name);
    }
    // Get duration from the audio file
    const audioEl = document.createElement('audio');
    audioEl.preload = 'metadata';
    audioEl.onloadedmetadata = () => {
      updateField('duration', Math.round(audioEl.duration));
      updateField('playback.end_at', Math.round(audioEl.duration));
    };
    audioEl.src = asset.file_url;
    setShowAssetLibrary(false);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Please enter a name for the audio.');
      return;
    }
    if (!formData.file_url) {
      setError('Please upload an audio file or enter a valid URL.');
      return;
    }

    await onSave(formData);
    onClose();
  };

  const isValid = formData.name.trim() && formData.file_url && (skipFolderSelect || formData.folder_id);

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#001233] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <Music className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {audio ? 'Edit Audio Element' : 'Add Audio Element'}
                </h2>
                <p className="text-sm text-gray-400">Upload or link audio to play during your session</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {readOnly && (
              <div className="flex items-start gap-3 mx-6 mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                <p className="text-sm text-purple-200">
                  This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
                </p>
              </div>
            )}
            <div className="p-6 space-y-6">
              {/* Error Display */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Source Selection */}
              <Tabs value={sourceTab} onValueChange={setSourceTab}>
                <TabsList className="grid grid-cols-3 w-full bg-white/5 rounded-lg p-1">
                  <TabsTrigger value="upload">
                    <Upload className="w-4 h-4" />
                    Upload
                  </TabsTrigger>
                  <TabsTrigger value="library">
                    <HardDrive className="w-4 h-4" />
                    Library
                  </TabsTrigger>
                  <TabsTrigger value="url">
                    <Link className="w-4 h-4" />
                    URL
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="upload" className="mt-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_FORMATS}
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  {!formData.file_url || formData.src_type !== 'upload' ? (
                    !readOnly && <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="w-full border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-green-500/50 transition-colors disabled:opacity-50"
                    >
                      {isUploading ? (
                        <div className="space-y-3">
                          <Loader2 className="w-10 h-10 text-green-500 mx-auto animate-spin" />
                          <p className="text-white font-medium">Uploading...</p>
                        </div>
                      ) : (
                        <>
                          <Music className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                          <p className="text-white font-medium">Click to upload audio</p>
                          <p className="text-xs text-gray-400 mt-1">MP3, WAV, AAC, OGG, M4A • Max {MAX_FILE_SIZE_MB}MB</p>
                        </>
                      )}
                    </button>
                  ) : (
                    <AudioPreview
                      src={formData.file_url}
                      duration={formData.duration}
                      fileSize={formData.file_size}
                      onRemove={readOnly ? undefined : () => {
                        updateField('file_url', '');
                        updateField('duration', 0);
                      }}
                      onReplace={readOnly ? undefined : () => fileInputRef.current?.click()}
                    />
                  )}
                </TabsContent>

                <TabsContent value="library" className="mt-4">
                  {formData.file_url && formData.src_type === 'upload' ? (
                    <AudioPreview
                      src={formData.file_url}
                      duration={formData.duration}
                      fileSize={formData.file_size}
                      onRemove={readOnly ? undefined : () => {
                        updateField('file_url', '');
                        updateField('duration', 0);
                      }}
                      onReplace={readOnly ? undefined : () => setShowAssetLibrary(true)}
                    />
                  ) : (
                    !readOnly && <button
                      onClick={() => setShowAssetLibrary(true)}
                      className="w-full border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-green-500/50 transition-colors"
                    >
                      <HardDrive className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                      <p className="text-white font-medium">Browse Asset Library</p>
                      <p className="text-xs text-gray-400 mt-1">Select from your uploaded audio files</p>
                    </button>
                  )}
                </TabsContent>

                <TabsContent value="url" className="mt-4 space-y-4">
                  <div className="flex gap-2">
                    <input
                      value={urlInput}
                      onChange={(e) => {
                        setUrlInput(e.target.value);
                        setUrlError('');
                      }}
                      placeholder="https://example.com/audio.mp3"
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-green-500 transition-colors"
                    />
                    <button
                      onClick={handleUrlValidate}
                      disabled={urlValidating || !urlInput.trim()}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm transition-colors disabled:opacity-50"
                    >
                      {urlValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Validate'}
                    </button>
                  </div>

                  {urlError && <p className="text-sm text-red-400">{urlError}</p>}

                  {formData.file_url && formData.src_type === 'url' && (
                    <AudioPreview
                      src={formData.file_url}
                      duration={formData.duration}
                      onRemove={readOnly ? undefined : () => {
                        updateField('file_url', '');
                        setUrlInput('');
                        updateField('duration', 0);
                      }}
                    />
                  )}
                </TabsContent>
              </Tabs>

              {/* Audio Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 mb-2 block text-sm">Audio Name *</label>
                  <input
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="e.g., Background Music"
                    disabled={readOnly}
                    className={`w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-green-500 transition-colors ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>

                {!skipFolderSelect && (
                  <div>
                    <label className="text-gray-400 mb-2 block text-sm">Folder *</label>
                    <Select value={formData.folder_id} onValueChange={(v) => updateField('folder_id', v)} placeholder="Select folder...">
                      {folders?.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </Select>
                  </div>
                )}

                <div>
                  <label className="text-gray-400 mb-2 block text-sm">Category</label>
                  <Select value={formData.category} onValueChange={(v) => updateField('category', v)} placeholder="Select category...">
                    {AUDIO_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="text-gray-400 mb-2 block text-sm">Description (optional)</label>
                  <input
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Internal notes..."
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-green-500 transition-colors"
                  />
                </div>
              </div>

              {/* Playback Settings */}
              <div className="space-y-4">
                <label className="text-white font-medium text-sm">Playback Settings</label>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Play className="w-4 h-4 text-green-400" />
                      <div>
                        <p className="text-white text-sm">Autoplay</p>
                        <p className="text-xs text-gray-400">Play when activated</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.playback.autoplay}
                      onCheckedChange={(v) => updateField('playback.autoplay', v)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Repeat className="w-4 h-4 text-blue-400" />
                      <div>
                        <p className="text-white text-sm">Loop</p>
                        <p className="text-xs text-gray-400">Repeat when finished</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.playback.loop}
                      onCheckedChange={(v) => updateField('playback.loop', v)}
                    />
                  </div>
                </div>

                {/* Volume */}
                <div className="p-4 bg-white/5 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Volume2 className="w-4 h-4 text-gray-400" />
                      <label className="text-white text-sm">Volume</label>
                    </div>
                    <span className="text-sm text-gray-400">{formData.playback.volume}%</span>
                  </div>
                  <Slider
                    value={[formData.playback.volume]}
                    onValueChange={([v]) => updateField('playback.volume', v)}
                    min={0}
                    max={100}
                    step={5}
                  />
                </div>

                {/* Ducking Control */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Volume2 className="w-4 h-4 text-amber-400" />
                    <div>
                      <p className="text-white text-sm">Audio Ducking</p>
                      <p className="text-xs text-gray-400">Reduce mic volume when playing</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.playback.ducking}
                    onCheckedChange={(v) => updateField('playback.ducking', v)}
                  />
                </div>

                {/* Start/End Time */}
                {formData.duration > 0 && (
                  <div className="p-4 bg-white/5 rounded-xl space-y-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <label className="text-white text-sm">Trim Audio</label>
                      <span className="text-xs text-gray-500">(Duration: {formatTime(formData.duration)})</span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Start At</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            max={formData.playback.end_at || formData.duration}
                            value={formData.playback.start_at || 0}
                            onChange={(e) => updateField('playback.start_at', parseInt(e.target.value) || 0)}
                            className="w-20 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-green-500"
                          />
                          <span className="text-gray-400 text-sm">sec</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">End At</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={formData.playback.start_at || 0}
                            max={formData.duration}
                            value={formData.playback.end_at || formData.duration}
                            onChange={(e) => updateField('playback.end_at', parseInt(e.target.value) || formData.duration)}
                            className="w-20 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-green-500"
                          />
                          <span className="text-gray-400 text-sm">sec</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            {readOnly ? (onCopyToMyElements && (
              <button
                type="button"
                onClick={onCopyToMyElements}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Copy to My Elements
              </button>
            )) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={!isValid}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {audio ? 'Save Changes' : 'Add Audio'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return (
    <>
      {ReactDOM.createPortal(modalContent, document.body)}
      <AssetLibraryModal
        isOpen={showAssetLibrary}
        onClose={() => setShowAssetLibrary(false)}
        onSelect={handleAssetSelect}
        filterType="audio"
      />
    </>
  );
}
